/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useEntityNavigation } from "@/hooks/useEntityNavigation";
import { toast } from "react-toastify";
import { debounce } from "lodash";
import { ISLA, SLAMode, SLAStartField } from "@/interfaces/slas";
import { listSLAs, showSLA, createSLA, updateSLA, deleteSLA } from "@/services";
import { updateValue } from "@/utils/updateValue";
import { useEntityQueryParams } from "@/hooks/useEntityQueryParams";
import { useEntitySearch } from "@/hooks/useEntitySearch";
import {
  notifyDeleteResults,
  runDeleteBatch,
} from "@/utils/deleteResults";

const defaultSLA: ISLA = {
  name: "",
  mode: SLAMode.ABSOLUTE_DATE,
  isActive: true,
  webhookUrl: "",
  notifications: []
};

export const slaModeOptions = [
  {
    value: SLAMode.ABSOLUTE_DATE,
    label: 'Data absoluta (comparação direta com campo)',
  },
  {
    value: SLAMode.RELATIVE,
    label: 'Relativo (baseado em campo + tempo)',
  },
  {
    value: SLAMode.MANUAL_DEADLINE,
    label: 'Manual (prazo definido pelo usuário)',
  },
];
export const slaStartFieldOptions = [
  {
    value: SLAStartField.PLANNED_DATE,
    label: 'Data planejada',
  },
  {
    value: SLAStartField.DEADLINE_DATE,
    label: 'Data limite',
  },
  {
    value: SLAStartField.CREATED,
    label: 'Data de criação',
  },
  {
    value: SLAStartField.STARTED,
    label: 'Data de início',
  },
];
export const useSLAs = ({
  disabledShowId,
  disabledGetAll,
  filter: filterInitial,
}: {
  filter?: Record<string, any>;
  disabledShowId?: boolean;
  disabledGetAll?: boolean;
}) => {
  const { idSLA: id } = useParams();
  const [slas, setSLAs] = useState<ISLA[]>([]);
  const [sla, setSLA] = useState<ISLA>(defaultSLA);
  const { filter, handleFilter, page, setPage, limit, setLimit } =
    useEntityQueryParams({ filter: filterInitial, defaultLimit: 25 });
  const [totalItems, setTotalItems] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);

  const { goAdd, goEdit, goBack } = useEntityNavigation({ basePath: "/towermanagement/slas" });

  const handleSLA = (key: string, value: any) => {
    setSLA((curr) => {
      const newData = updateValue(curr, key, value);
      return { ...curr, ...newData };
    });
  };

  const getAll = useCallback(
    debounce(
      ({
        page = 1,
        limit,
        filter,
      }: {
        page?: number;
        limit?: number;
        filter?: any;
      }) => {
        setLoading(true);
        listSLAs({
          includes: ["createdBy", "updatedBy"],
          page,
          filter,
          limit,
        })
          .then(({ slas: listSLAs, currentPage, totalItems, limit }) => {
            setSLAs(listSLAs);
            
            setLimit(Number(limit));
            setTotalItems(Number(totalItems));
          })
          .finally(() => setLoading(false));
      },
      150
    ),
    []
  );

  const get = ({ id }: { id: string | number }) => {
    setLoading(true);
    showSLA({ id })
      .then(({ sla: slaShow }) => {
        setSLA(slaShow);
      })
      .finally(() => setLoading(false));
  };

  const search = useEntitySearch({ list: listSLAs, key: "slas", filter });

  const create = () => {
    setLoading(true);
    createSLA({ sla })
      .then(({ sla: slaCreated }) => {
        toast.success(`SLA (${slaCreated.name}) criado com sucesso!`);
        goEdit(slaCreated.id!);
      })
      .finally(() => setLoading(false));
  };

  const update = () => {
    setLoading(true);
    updateSLA({ id: sla.id!, sla })
      .then(({ sla: slaUpdated }) => {
        toast.success(`SLA (${slaUpdated.name}) atualizado com sucesso!`);
        goEdit(slaUpdated.id!);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!disabledGetAll) getAll({ page, limit, filter });
  }, [page, limit, filter]);

  useEffect(() => {
    if (id && !disabledShowId) {
      if (id !== "new") get({ id });
    }
  }, []);

  const deleteMany = async (ids: (string | number)[]) => {
    if (!ids?.length) return;
    setLoading(true);

    try {
      const results = await runDeleteBatch(ids, (entityId) =>
        deleteSLA({ id: entityId }),
      );

      notifyDeleteResults(results, {
        singular: "SLA",
        plural: "SLAs",
        gender: "m",
      });
    } catch {
      // erro sem resposta do servidor (rede/timeout): o interceptor já avisou
    } finally {
      setLoading(false);
      getAll({ page, limit, filter });
    }
  };

  return {
    deleteMany,
    slas,
    sla,
    loading,
    setPage,
    setLimit,
    page,
    limit,
    goEdit,
    goBack,
    goAdd,
    handleSLA,
    create,
    id,
    update,
    setTotalItems,
    totalItems,
    handleFilter,
    search,
  };
};
